/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-02 12:07:54
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { FormLayout, SkeletonDisplayText, Icon, Button, TextStyle, Link } from '@shopify/polaris';
import { MobileVerticalDotsMajor } from '@shopify/polaris-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { URL } from 'src/constants/links';
import OptionsTableCell from './options-table-cell';
import { getLabelSearchField } from 'src/helpers/search-settings-helper';

type Props = {
  fields: Object,
  loading: boolean,
  searchFields: Object,
  resetDefault: () => void,
  emailSupport: string
};

type ComposedProps = Props & WithI18nProps;

class SearchByOptions extends Component<ComposedProps> {
  onDragEnd = (result: DropResult) => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
  };

  render() {
    const { i18n, loading, fields, searchFields, resetDefault, emailSupport } = this.props;

    return (
      <FormLayout>
        <div className="btn-reset">
          <Button plain size="slim" onClick={resetDefault}>
            {i18n.translate('AdminHTML.Common.Action.reset')}
          </Button>
        </div>
        <p>{i18n.translate('AdminHTML.SearchSettings.SearchFields.description')}</p>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <div
            className="Polaris-DataTable BC-DataTable__SearchSettings"
            style={{
              borderBottom: '0.0625rem solid var(--p-divider)',
              borderTop: '0.0625rem solid var(--p-divider)'
            }}>
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                {loading ? (
                  <tbody>
                    {[...Array(5).keys()].map((item, key) => (
                      <tr key={key}>
                        <td className="Polaris-DataTable__Cell" key={key}>
                          <SkeletonDisplayText size="small" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : !_.isEmpty(fields) ? (
                  <Droppable droppableId="table">
                    {provided => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {fields.map((item, key) => {
                          const { name } = searchFields[key] || { name: 'name' };
                          return (
                            // Enable drag for per option row
                            <Draggable draggableId={name} key={`${name}-${key}`} index={key}>
                              {(provided, snapshot) => (
                                <tr
                                  className="Polaris-DataTable__TableRow"
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}>
                                  {/* Drag icon */}

                                  {/* Drag icon */}
                                  <OptionsTableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                    <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                  </OptionsTableCell>

                                  {/* Status */}
                                  <OptionsTableCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <Field
                                      name={`${item}.status`}
                                      component={RenderToggleSwitch}
                                      label={
                                        getLabelSearchField(searchFields, key)
                                          ? i18n.translate(getLabelSearchField(searchFields, key))
                                          : ''
                                      }
                                    />
                                  </OptionsTableCell>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                ) : (
                  <tbody />
                )}
              </table>
            </div>
          </div>
        </DragDropContext>

        <p>
          {i18n.translate('AdminHTML.SearchSettings.SearchFields.content_Using', {
            t1: (
              <Link external url={URL['SearchByMetafieldDoc']}>
                {i18n.translate('AdminHTML.SearchSettings.SearchFields.content_Using_t1')}
              </Link>
            )
          })}
          <br />
          {i18n.translate('AdminHTML.SearchSettings.SearchFields.content_Contact', {
            nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
          })}
        </p>
      </FormLayout>
    );
  }
}
export default withI18n()(SearchByOptions);
